import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import { FaChevronDown, FaChevronUp, FaFilter, FaSearch, FaSort, FaTimes } from "react-icons/fa";
import { ShoppingCart } from "lucide-react";
import { useLanguage } from "./LanguageContext";
import FavoriteButton from "./FavoriteButton";
import { useDataRefresh } from "./liveUpdates/DataRefreshProvider";
import { api } from "../lib/http";

const API = "https://shopaholics.ge/api";

/* ================= THEME ================= */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  card: "#fff",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

/* ================= LOADER ================= */
const spin = keyframes`to{transform:rotate(360deg)}`;
const PageLoader = styled.div`display:grid;place-items:center;min-height:60vh;`;
const Spinner = styled.div`
  width:42px;height:42px;border-radius:50%;
  border:4px solid rgba(0,0,0,.1);border-top-color:${COLORS.text};
  animation:${spin} 1s linear infinite;
`;

/* ================= LAYOUT ================= */
const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 12px clamp(10px,4vw,16px) 28px;
  overflow-x: hidden !important;
`;
const GridWrap = styled(Container)`
  padding-top: 18px; padding-left:0; padding-right:0;
  @media (min-width: 641px){ padding-left:16px; padding-right:16px; }
`;

/* ================= TOP BAR (sticky) ================= */
const TopBar = styled.div`
  position: sticky; top: 66px; z-index: 5;
  background: rgba(255,255,255,.85);
  backdrop-filter: saturate(180%) blur(8px);
  border-bottom: 1px solid ${COLORS.line};
`;
const TopBarInner = styled(Container)`
  display:flex;align-items:center;justify-content:space-between;gap:10px;
  padding-top:10px;padding-bottom:10px;
  overflow: visible !important;
`;
const LeftActions = styled.div`display:flex;gap:10px;flex-wrap:wrap;`;
const RightActions = styled.div`position:relative;`;

/* Pills / Dropdowns */
const PillButton = styled.button`
  appearance:none;border:1px solid ${COLORS.line};
  background:#fff;color:${COLORS.text};
  border-radius:999px;height:40px;padding:0 14px;
  display:inline-flex;align-items:center;gap:8px;
  font-weight:700;cursor:pointer;
  transition: box-shadow .15s ease, transform .15s ease, border-color .15s ease;
  &:hover{ box-shadow:${COLORS.shadow}; transform:translateY(-1px); }
`;

const SortDropdownWrap = styled.div`position:relative;`;
const DropdownCard = styled.div`
  position:absolute;top:calc(100% + 8px);right:0;
  min-width:220px;background:#fff;border:1px solid ${COLORS.line};
  border-radius:14px;box-shadow:${COLORS.shadow};padding:8px;z-index:20;
  ${({$hidden}) => $hidden && css`display:none;`}
`;
const DropdownItem = styled.button`
  width:100%;text-align:left;border:0;background:transparent;
  padding:10px 12px;border-radius:10px;font-weight:600;cursor:pointer;
  &:hover{ background:${COLORS.subtle}; }
`;

/* ================= APPLIED FILTERS ================= */
const AppliedFiltersBox = styled(Container)`
  padding-top:10px;display:flex;flex-wrap:wrap;gap:8px;align-items:center;
`;
const FilterTag = styled.div`
  display:inline-flex;align-items:center;gap:8px;
  background:#f0f0f1;color:${COLORS.text};
  border:1px solid ${COLORS.line};border-radius:999px;
  padding:6px 10px;font-size:13px;
  .close{display:inline-flex;align-items:center;cursor:pointer;opacity:.7;}
  .close:hover{opacity:1;}
`;

/* ================= OVERLAY + DRAWER ================= */
const Overlay = styled.div`
  position:fixed;inset:0;background:rgba(0,0,0,.35);z-index:3005;
  opacity:${({$open}) => $open ? 1 : 0};
  visibility:${({$open}) => $open ? 'visible' : 'hidden'};
  transition:.2s;
`;

const FiltersMenu = styled.aside`
  position:fixed;top:0;left:0;height:100dvh;width:86vw;max-width:360px;
  background:#fff;border-right:1px solid ${COLORS.line};
  transform: translateX(${({isVisible})=>isVisible?'0':'-100%'});
  transition: transform .25s ease; z-index:3006;
  display:grid;grid-template-rows:auto 1fr auto;overflow:hidden;
  -webkit-overflow-scrolling:touch;overscroll-behavior:contain;
`;

const FilterTop = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:14px 16px;border-bottom:1px solid ${COLORS.line};
  h3{margin:0;font-size:16px;}
`;
const CloseButton = styled.button`
  background:#fff;border:1px solid ${COLORS.line};border-radius:10px;
  height:36px;width:36px;display:grid;place-items:center;cursor:pointer;
`;

const FilterBody = styled.div`
  padding:12px 14px 18px;display:grid;gap:14px;min-height:0;
  overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;
`;
const FilterSection = styled.div`border:1px solid ${COLORS.line};border-radius:12px;background:#fff;`;
const FilterHeaderBtn = styled.button`
  width:100%;border:0;background:#fff;padding:14px;
  display:flex;align-items:center;justify-content:space-between;
  font-weight:800;cursor:pointer;user-select:none;
`;
const FilterOptions = styled.div`
  display:flex;flex-direction:column;overflow:hidden;
  max-height:${({isExpanded,$tall})=>isExpanded?($tall?'68vh':'52vh'):'0'};
  pointer-events:${({isExpanded})=>isExpanded?'auto':'none'};
  transition:max-height .25s ease;

  > .content{position:sticky;top:0;padding:12px;background:#fff;z-index:1;border-bottom:1px solid rgba(0,0,0,.06);}
  > .scroll{
    flex:1;min-height:0;overflow-y:auto;padding:8px 12px 12px;
    -webkit-overflow-scrolling:touch;overscroll-behavior:contain;touch-action:pan-y;
    scrollbar-width:thin;
  }
  > .scroll::-webkit-scrollbar{width:8px;}
  > .scroll::-webkit-scrollbar-thumb{background:${COLORS.brand};border-radius:8px;}
  @media(max-width:768px){ max-height:${({isExpanded})=>isExpanded?'60vh':'0'}; }
`;

const SearchContainer = styled.div`
  display:flex;align-items:center;gap:8px;
  border:1px solid ${COLORS.line};border-radius:10px;padding:8px 10px;background:#fff;
  input{border:0;outline:0;flex:1;font-size:14px;}
  svg{color:${COLORS.dim};}
`;
const SearchInput = styled.input``;

const CheckboxList = styled.div`display:grid;gap:10px;`;
const CheckboxItem = styled.label`
  display:grid;grid-template-columns:22px 1fr;align-items:center;gap:10px;cursor:pointer;font-size:14px;color:${COLORS.text};
  input{display:none;}
  input + span{width:20px;height:20px;border:1px solid #cfcfcf;border-radius:4px;background:#fff;display:inline-block;position:relative;}
  input:checked + span{background:${COLORS.brand};border-color:${COLORS.brand};}
  input:checked + span::after{content:'';position:absolute;inset:4px;border:2px solid #fff;border-top:0;border-left:0;transform:rotate(45deg);}
`;

const PriceFilterContainer = styled.div`
  background:#fafafa;border:1px solid ${COLORS.line};border-radius:10px;padding:12px;display:grid;gap:12px;
`;
const PriceRangeWrapper = styled.div`display:grid;gap:6px;`;
const PriceLabel = styled.label`font-weight:800;font-size:14px;display:flex;justify-content:space-between;color:${COLORS.text};`;
const PriceRangeInput = styled.input`
  width:100%;height:6px;border-radius:999px;outline:none;-webkit-appearance:none;
  background:linear-gradient(to right, ${COLORS.brand} 0%, #e7c2c2 100%);
  &::-webkit-slider-thumb{appearance:none;width:18px;height:18px;border-radius:50%;background:${COLORS.brand};cursor:pointer;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.2);}
`;
const DrawerFooter = styled.div`border-top:1px solid ${COLORS.line};padding:12px;display:grid;gap:8px;`;
const ClearFiltersButton = styled.button`
  width:100%;height:44px;border-radius:12px;border:1px solid ${COLORS.line};
  background:${COLORS.text};color:#fff;font-weight:800;cursor:pointer;
  transition:filter .15s ease, transform .15s ease;
  &:hover{ filter:brightness(.95); transform:translateY(-1px); }
`;

/* ================= GRID / CARDS ================= */
const PerfumeGrid = styled.div`
  display:grid;gap:16px;
  grid-template-columns:repeat(5,minmax(0,1fr));
  @media (max-width:1200px){grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;}
  @media (max-width:900px){grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;}
  @media (max-width:640px){grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;}
  @media (max-width:380px){grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;}
`;

const PerfumeCard = styled.div`
  background: ${COLORS.card};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  padding: 12px;
  position: relative;
  transition: box-shadow .2s ease, transform .2s ease, border-color .2s ease;
  cursor: pointer;
  min-width: 0; box-sizing: border-box;

  &:hover { box-shadow: ${COLORS.shadow}; transform: translateY(-2px); border-color: rgba(0,0,0,.12); }

  .hover-options {
    display: ${({ $active }) => ($active ? 'flex' : 'none')};
    position: absolute; inset: 0;
    background: rgba(255,255,255,.6); backdrop-filter: blur(6px);
    align-items: center; justify-content: center; z-index: 2; border-radius: 16px;
  }

  .view-btn{
    border: 1px solid ${COLORS.line}; background:#fff; border-radius: 12px; height: 38px; padding: 0 12px; font-weight: 800; cursor: pointer;
    transition: transform .15s ease, box-shadow .15s ease; color: black;
  }
  .view-btn:hover{ transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }

  @media (max-width: 640px) { padding: 8px; border-radius: 12px; }
  @media (max-width: 380px) { padding: 6px; border-radius: 10px; }
`;

const ImgWrap = styled.div`
  width:100%;aspect-ratio:1/1;border-radius:12px;background:${COLORS.subtle};
  display:grid;place-items:center;overflow:hidden;margin-bottom:10px;position:relative;
  @media (max-width:640px){ margin-bottom:6px; }
`;
const PerfumeImage = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;
  object-position: center;
  display: block;
`;

const PerfumeName = styled.h3`
  font-size:14px;font-weight:800;color:${COLORS.text};
  margin:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  @media(max-width:640px){font-size:13px;}
`;
const PerfumeBrand = styled.div`font-size:12px;color:${COLORS.dim};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;

const PriceBlock = styled.div`
  margin-top:8px;font-weight:800;color:${COLORS.text};font-size:13px;
  .old{ color:#9ca3af; text-decoration:line-through; margin-right:6px; }
  @media(max-width:640px){font-size:12px;}
`;

const blink = keyframes` 0%{opacity:1} 50%{opacity:.2} 100%{opacity:1} `;
const SaleBadge = styled.div`
  position: absolute; top: 10px; right: 10px;
  background: #ef4444; color: #fff; font-weight: 800;
  padding: 6px 10px; border-radius: 999px; font-size: 11px;
  box-shadow: 0 6px 16px rgba(239,68,68,.35);
  animation: ${blink} 1.5s infinite;
  z-index: 100;

  @media (max-width: 640px) { top: 8px; right: 8px; font-size: 10px; padding: 5px 8px; }
`;

const IconBar = styled.div`
  position:absolute;left:10px;top:10px;display:flex;gap:8px;z-index:3;
  .icon-btn{
    width:36px;height:36px;border-radius:10px;border:1px solid ${COLORS.line};
    background:#fff;display:grid;place-items:center;cursor:pointer;
    transition:transform .15s ease, box-shadow .15s ease;
  }
  .icon-btn:hover{ transform:translateY(-1px); box-shadow:${COLORS.shadow}; }
`;

const CardLink = styled(Link)`text-decoration:none;color:inherit;`;
const CardBody = styled.div`text-decoration:none;color:inherit;`;

const LoadMoreButton = styled.button`
  display:block;margin:24px auto 40px;height:44px;padding:0 16px;
  background:${COLORS.text};color:#fff;border:0;border-radius:12px;font-weight:800;cursor:pointer;
  transition:transform .15s ease, filter .15s ease;
  &:hover{ transform:translateY(-1px); filter:brightness(.95); }
`;

/* ================= HELPERS ================= */

// tiny utils
const arr = (x)=>Array.isArray(x)?x:[];
const num = (x,d=0)=> (x==null||x===""||isNaN(+x)?d:+x);
const toMlNum = (v) => {
  const n = parseFloat(String(v ?? "").toLowerCase().replace(/[^\d.]/g, ""));
  return isNaN(n) ? 0 : n;
};
const coverOf = (c)=> c?.cover_image || "/images/placeholder/collection-cover.jpg";

// Accessory helpers
const variantHasSale = (v={})=>{
  const price = Number(v.price ?? 0);
  const sale  = Number(v.sale_p ?? v.sale_price ?? 0);
  return sale > 0 && sale < price;
};
const variantEffPrice = (v={})=>{
  const price = Number(v.price ?? 0);
  const sale  = Number(v.sale_p ?? v.sale_price ?? 0);
  return (sale>0 && sale<price) ? sale : price;
};
const pickFirstInStockVariant = (variants=[]) =>
  variants.find(v => Number(v.is_active ?? 1) === 1 && Number(v.stock ?? 0) > 0) || variants[0] || null;

const pickFirstAccessoryVariant = (p = {}) => {
  const vs = Array.isArray(p.accessory_variants) ? p.accessory_variants : [];
  if (!vs.length) return null;

  const parseDate = (d) => (d ? Date.parse(d) || Infinity : Infinity);
  const normNum = (n) => (n == null ? Infinity : Number(n));

  const sorted = [...vs].sort((a, b) => {
    const ca = parseDate(a.created_at), cb = parseDate(b.created_at);
    if (ca !== cb) return ca - cb;
    const sa = normNum(a.size_sort), sb = normNum(b.size_sort);
    if (sa !== sb) return sa - sb;
    const ia = normNum(a.id), ib = normNum(b.id);
    return ia - ib;
  });

  const active  = sorted.filter(v => Number(v.is_active ?? 1) === 1);
  const inStock = active.filter(v => Number(v.stock ?? v.unit_stock ?? 0) > 0);
  return inStock[0] || active[0] || sorted[0] || null;
};

// Per-ML image
const pickFirstMlImage = (p = {}) => {
  const mls = Array.isArray(p.milliliters) ? p.milliliters : [];
  for (const ml of mls) {
    const imgs = Array.isArray(ml.images) ? ml.images : [];
    if (!imgs.length) continue;
    const first = imgs[0];
    if (typeof first === "string" && first.trim()) return first;
    if (first?.image_url?.trim()) return first.image_url;
    if (first?.url) return first.url;
    if (first?.src) return first.src;
  }
  return null;
};
const cardImage = (p) => {
  if (p.category === "accessories") {
    const v = pickFirstAccessoryVariant(p);
    if (Array.isArray(v?.images) && v.images[0]) return v.images[0];
  }
  if (["atomizers","skin-care","hair-and-body-care","perfume"].includes(String(p.category||"").toLowerCase())) {
    const u = pickFirstMlImage(p);
    if (u) return u;
  }
  return p.imageUrls?.[0] || p.image_url || "/images/placeholder/product.jpg";
};

// STOCK STRATEGY
// ─────────────────────────────────────────────
// STOCK STRATEGY + FULL-FIRST picker (respect row-level stock)
// ─────────────────────────────────────────────
const mlStrategy = (ml, item) => {
  if (ml?.stock_strategy === "units") return "units";
  if (ml?.stock_strategy === "full")  return "full";
  if (Number(ml?.unit_stock ?? 0) > 0) return "units";
  if (item?.per_ml_stock) return "units";
  return "full";
};

// FULL + UNIT კომბო ლოგიკა (პატარა ზომას ვანიჭებთ უპირატესობას):
// FULL + UNIT კომბო ლოგიკა (პატარა ზომას ვანიჭებთ უპირატესობას) — FULL-FIRST
// PICKER: პრიორიტეტი ყველაზე პატარა ML-ს — სტრატეგია მხოლოდ ხელმისაწვდომობის შესამოწმებლად
const pickDefaultMlForQuickAdd = (item) => {
  const mls = Array.isArray(item?.milliliters) ? item.milliliters : [];
  if (!mls.length) return null;

  const fullTotal = Number(item.full_stock ?? 0);

  const rows = mls
    .filter(m => Number(m.size) > 0)
    .map(m => ({
      ...m,
      size: Number(m.size),
      unit_stock: Number(m.unit_stock ?? 0),
      stock: Number(m.stock ?? 0),            // row-level stock (full რეჟიმისთვის)
      price: Number(m.price ?? 0),
      sale_price: m.sale_price != null ? Number(m.sale_price) : null,
      images: Array.isArray(m.images) ? m.images : [],
      _strat: String(m.stock_strategy ?? "").toLowerCase(), // 'units' | 'full' | 'hybrid'
    }))
    .sort((a, b) => a.size - b.size); // ← უმცირესი ზომა წინ

  for (const r of rows) {
    const isUnitsOK  = (r._strat === 'units'  && r.unit_stock > 0);
    const isFullOK   = (r._strat === 'full'   && r.stock > 0 && fullTotal >= r.size);
    const isHybridOK = (r._strat === 'hybrid' && (
                          r.unit_stock > 0 ||
                          (r.stock > 0 && fullTotal >= r.size)
                        ));

    if (isUnitsOK || isFullOK || isHybridOK) {
      // თუ ჰიბრიდია და ორივე გზით ხელმისაწვდომია — მაინც ამ ზომას ვირჩევთ,
      // `_from` ვანიშნოთ იმაზე რაც ფაქტობრივად გვამუშავებს (იუნიტი თუ ფულ).
      let from = 'full';
      if (r._strat === 'units') from = 'unit';
      else if (r._strat === 'hybrid') from = (r.unit_stock > 0) ? 'unit' : 'full';
      else if (r._strat === 'full') from = 'full';

      return { ...r, _from: from };
    }
  }

  return null; // არც ერთი ზომა არ გამოდგა
};




const canQuickAdd = (item) => !!pickDefaultMlForQuickAdd(item);

const getDefaultMlSelection = (p = {}) => {
  const picked = pickDefaultMlForQuickAdd(p);
  if (!picked) return null;

  return {
    ...picked,
    size: Number(picked.size),
    price: Number(picked.price || 0),
    sale_price: picked.sale_price != null ? Number(picked.sale_price) : null,
    images: Array.isArray(picked.images) ? picked.images : [],
    unit_stock: Number(picked.unit_stock ?? 0),
    _from: picked._from, // 'unit' | 'full' (თუ გინდა UI-ში მიუთითო)
  };
};


// normalize one product
const normalizeApiProduct = (p) => {
  const imageUrls = Array.isArray(p.imageUrls)
    ? p.imageUrls
    : Array.isArray(p.images)
      ? p.images
      : (typeof p.imageUrls === "string" && p.imageUrls.length
          ? p.imageUrls.split(",").map(s => s.trim()).filter(Boolean)
          : []);

  const milliliters = Array.isArray(p.milliliters)
    ? p.milliliters.map(ml => {
        const raw = ml.ml ?? ml.size ?? "";
        return {
          id: ml.id,
          ml: raw,
          size: toMlNum(raw),
          price: String(ml.price ?? "0").replace(",", "."),
          sale_price: String(ml.sale_price ?? "").replace(",", "."),
          stock: Number(ml.stock ?? 0),
          unit_stock: Number(ml.unit_stock ?? 0),
          stock_strategy: String(ml.stock_strategy ?? "").toLowerCase(),
          images: Array.isArray(ml.images)
            ? ml.images
            : (typeof ml.images === "string" && ml.images.length
                ? ml.images.split(",").map(s => s.trim()).filter(Boolean)
                : []),
        };
      })
    : [];

  const accessory_variants = Array.isArray(p.accessory_variants)
    ? p.accessory_variants.map(v => ({
        id: v.id,
        color_ka: v.color_ka ?? v.color ?? null,
        color_en: v.color_en ?? null,
        size_label: v.size_label ?? null,
        stock: Number(v.stock ?? 0),
        price: Number(v.price ?? 0),
        sale_p: Number(v.sale_p ?? v.sale_price ?? 0),
        is_active: Number(v.is_active ?? 1),
        images: Array.isArray(v.images)
          ? v.images
          : (typeof v.images === "string" && v.images.length
              ? v.images.split(",").map(s => s.trim()).filter(Boolean)
              : []),
      }))
    : [];

  const name = p.name ?? p.title ?? p.product_name ?? "";
  const brand = p.brand ?? p.Brand ?? "";

  const derivedFullStockMl = milliliters.reduce((sum, ml) => sum + (Number(ml.stock) * (ml.size || 0)), 0);
  const derivedFullStockAcc = accessory_variants.reduce((s,v)=> s + Number(v.stock||0), 0);

  return {
    ...p,
    product_id: p.product_id ?? p.id ?? p.pid,
    name, brand,
    imageUrls, milliliters, accessory_variants,
    price_p: Number(p.price_p ?? 0),
    sale_p: Number(p.sale_p ?? 0),
    full_stock: Number(p.full_stock ?? p.stock ?? p.quantity ?? p.qty ?? 0) || (p.category==='accessories' ? derivedFullStockAcc : derivedFullStockMl),
    per_ml_stock: Boolean(p.per_ml_stock ?? false),
    category: (p.category ?? p.type ?? "").toLowerCase(),
    aroma_en: p.aroma_en ?? p.aroma ?? "",
  };
};

// price text
const priceRangeText = (p, t) => {
  if (p.category === "accessories") {
    const v = pickFirstInStockVariant(arr(p.accessory_variants));
    if (!v) return "N/A";
    const regular = Number(v.price ?? v.price_p ?? 0);
    const sale    = Number(v.sale_p ?? 0);
    const onSale  = sale > 0 && sale < regular;
    if (!regular) return "N/A";
    return onSale
      ? (<><span className="old">{regular} {t("newproducts.currency")}</span><span>{sale} {t("newproducts.currency")}</span></>)
      : (<span>{regular} {t("newproducts.currency")}</span>);
  }

  const prices = arr(p.milliliters).map(ml => num(ml.price)).filter(v=>v>0);
  const salePrices = arr(p.milliliters).map(ml => num(ml.sale_price || ml.price)).filter(v=>v>0);
  if (!prices.length && (p.price_p || p.sale_p)) {
    const base = Number(p.price_p || 0), s = Number(p.sale_p || 0);
    if (s>0 && s<base) return (<><span className="old">{base} {t("newproducts.currency")}</span><span>{s} {t("newproducts.currency")}</span></>);
    return (<span>{base} {t("newproducts.currency")}</span>);
  }
  const minOriginal = prices.length ? Math.min(...prices) : 0;
  const maxOriginal = prices.length ? Math.max(...prices) : 0;
  const minSale = salePrices.length ? Math.min(...salePrices) : 0;
  const maxSale = salePrices.length ? Math.max(...salePrices) : 0;
  const changed = (minSale < minOriginal) || (maxSale < maxOriginal);

  if (changed) {
    return (
      <>
        <span className="old">
          {minOriginal === maxOriginal
            ? `${minOriginal} ${t("newproducts.currency")}`
            : `${minOriginal} - ${maxOriginal} ${t("newproducts.currency")}`}
        </span>
        <span>
          {minSale === maxSale
            ? `${minSale} ${t("newproducts.currency")}`
            : `${minSale} - ${maxSale} ${t("newproducts.currency")}`}
        </span>
      </>
    );
  }
  return (
    <span>
      {minOriginal === maxOriginal
        ? `${minOriginal} ${t("newproducts.currency")}`
        : `${minOriginal} - ${maxOriginal} ${t("newproducts.currency")}`}
    </span>
  );
};

/* ================= PAGE ================= */

const CollectionPage = ({ addToCart }) => {
  const { version } = useDataRefresh();
  const { slugOrId } = useParams();
  const { t, language } = useLanguage();

  const [collection, setCollection] = useState(null);
  const [perfumes, setPerfumes] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI / Filters
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [sortDropdownVisible, setSortDropdownVisible] = useState(false);
  const [sortOrder, setSortOrder] = useState("");

  const [aromas, setAromas] = useState([]);
  const [aromaExpanded, setAromaExpanded] = useState(false);
  const [aromaSearch, setAromaSearch] = useState("");
  const [selectedAromas, setSelectedAromas] = useState([]);

  const [selectedGenders, setSelectedGenders] = useState([]);
  const [genderExpanded, setGenderExpanded] = useState(false);

  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryExpanded, setCategoryExpanded] = useState(false);

  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);
  const [priceRange, setPriceRange] = useState([0,0]);
  const [isPriceActive, setIsPriceActive] = useState(false);

  const [applied, setApplied] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const [visible, setVisible] = useState(40);

  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  const [silentRefreshing, setSilentRefreshing] = useState(false);
  const firstLoadRef = useRef(true);
  const lastSilentRefetch = useRef(0);

  /* ---------- title ---------- */
  useEffect(() => {
    const titleField = t("slider.name");
    const title = collection?.[titleField] || collection?.name_ka || collection?.name_en || (language==="ka" ? "კოლექცია" : "Collection");
    document.title = `${title} | Shopaholics`;
  }, [collection, t, language]);

  /* ---------- outside clicks ---------- */
  useEffect(() => {
    const handleGlobalMouseDown = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setSortDropdownVisible(false);
      if (menuRef.current && !menuRef.current.contains(e.target)) setFiltersVisible(false);
      const insideCard = e.target.closest('[data-card="true"]');
      if (!insideCard) setActiveCard(null);
    };
    document.addEventListener('mousedown', handleGlobalMouseDown);
    return () => document.removeEventListener('mousedown', handleGlobalMouseDown);
  }, []);

  /* ---------- body scroll lock while drawer open ---------- */
  useEffect(() => {
    if (!filtersVisible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [filtersVisible]);

  /* ---------- fetch collection + items + products ---------- */
  useEffect(()=> {
    let cancelled = false;
    (async ()=>{
      try {
        setLoading(true);

        // resolve collection
        let collection_id = null;
        let collectionObj = null;

        if (/^\d+$/.test(slugOrId)) {
          collection_id = String(slugOrId);
        } else {
          const listRes = await api.get(`${API}/collections/list.php`, { params: { v: version } });
          const list = Array.isArray(listRes.data) ? listRes.data : (listRes.data?.data || listRes.data?.collections || []);
          collectionObj = list.find(x => String(x.slug) === String(slugOrId));
          if (!collectionObj) throw new Error("Collection not found");
          collection_id = String(collectionObj.id);
        }

        if (!collectionObj) {
          const listRes = await api.get(`${API}/collections/list.php`, { params: { v: version } });
          const list = Array.isArray(listRes.data) ? listRes.data : (listRes.data?.data || listRes.data?.collections || []);
          collectionObj = list.find(x => String(x.id) === String(collection_id)) || null;
        }
        if (cancelled) return;
        setCollection(collectionObj);

        // items -> product ids
        const itemsRes = await api.get(`${API}/collections/items.php`, { params: { collection_id, v: version } });
        const itemsArr = Array.isArray(itemsRes.data) ? itemsRes.data : (itemsRes.data?.data || itemsRes.data?.items || []);
        const productIdSet = new Set(itemsArr.map(it => String(it.product_id ?? it.id ?? it.pid)).filter(Boolean));

        // full products (with ml + accessory variants)
        const fullRes = await api.get(`${API}/get_products_collections.php`, { params: { v: version } });
        const full = Array.isArray(fullRes.data) ? fullRes.data : [];
        const normalized = full.map(normalizeApiProduct);
        const onlyCollection = normalized.filter(p => productIdSet.has(String(p.product_id)));

        if (cancelled) return;

        setPerfumes(onlyCollection);
        setFiltered(onlyCollection);

        // derive filter ranges
        const allPrices = onlyCollection.flatMap(p => {
          if (p.category === "accessories") {
            const vs = arr(p.accessory_variants);
            return vs.length ? vs.map(v => variantEffPrice(v)) : [];
          }
          const ml = arr(p.milliliters);
          return ml.length ? ml.map(x => num(x.sale_price || x.price, 0)) : [];
        }).filter(v => v > 0);

        const minP = allPrices.length ? Math.min(...allPrices) : 0;
        const maxP = allPrices.length ? Math.max(...allPrices) : 0;
        setMinPrice(minP); setMaxPrice(maxP); setPriceRange([minP, maxP]);

        setAromas([
          ...new Set(
            onlyCollection.flatMap(p => p.aroma_en ? p.aroma_en.split(",").map(a => a.trim()) : [])
          )
        ]);

        const cats = [...new Set(onlyCollection.map(p => p.category).filter(Boolean))];
        setCategories(cats);

      } catch {
        setCollection(null);
        setPerfumes([]); setFiltered([]);
      } finally {
        setLoading(false);
        firstLoadRef.current = false;
      }
    })();
    return ()=>{ cancelled = true; };
  }, [slugOrId, language, version]);

  /* ---------- silent refresh on version change ---------- */
  useEffect(() => {
    if (firstLoadRef.current) return;
    const now = Date.now();
    if (now - lastSilentRefetch.current < 800) return;
    lastSilentRefetch.current = now;

    let cancelled = false;
    (async ()=>{
      try{
        setSilentRefreshing(true);

        let collection_id =
          collection?.id
            ? String(collection.id)
            : (/^\d+$/.test(slugOrId) ? String(slugOrId) : null);

        if (!collection_id) {
          const listRes = await api.get(`${API}/collections/list.php`, { params: { v: version } });
          const list = Array.isArray(listRes.data) ? listRes.data : (listRes.data?.data || listRes.data?.collections || []);
          const c = /^\d+$/.test(slugOrId)
            ? list.find(x => String(x.id) === String(slugOrId)) || null
            : list.find(x => String(x.slug) === String(slugOrId)) || null;
          if (c) collection_id = String(c.id);
        }
        if (cancelled || !collection_id) return;

        const itemsRes = await api.get(`${API}/collections/items.php`, { params: { collection_id, v: version } });
        const itemsArr = Array.isArray(itemsRes.data) ? itemsRes.data : (itemsRes.data?.data || itemsRes.data?.items || []);
        const productIdSet = new Set(itemsArr.map(it => String(it.product_id ?? it.id ?? it.pid)).filter(Boolean));

        const fullRes = await api.get(`${API}/get_products_collections.php`, { params: { v: version } });
        const full = Array.isArray(fullRes.data) ? fullRes.data : [];
        const normalized = full.map(normalizeApiProduct);
        const onlyCollection = normalized.filter(p => productIdSet.has(String(p.product_id)));
        if (cancelled) return;

        setPerfumes(onlyCollection);

        // update min/max price safely
        const allPrices = onlyCollection.flatMap(p => {
          if (p.category === "accessories") {
            const vs = arr(p.accessory_variants);
            return vs.length ? vs.map(v => variantEffPrice(v)) : [];
          }
          const ml = arr(p.milliliters);
          return ml.length ? ml.map(x => num(x.sale_price || x.price, 0)) : [];
        }).filter(v => v > 0);
        const minP = allPrices.length ? Math.min(...allPrices) : 0;
        const maxP = allPrices.length ? Math.max(...allPrices) : 0;
        setMinPrice(x => x !== minP ? minP : x);
        setMaxPrice(x => x !== maxP ? maxP : x);
      } finally {
        if (!cancelled) setSilentRefreshing(false);
      }
    })();

    return ()=>{ cancelled = true; };
  }, [version]); // silent updates

  /* ---------- filter + sort ---------- */
  const filteredMemo = useMemo(()=>{
    let f = perfumes;

    if (isPriceActive) {
      f = f.filter(p => {
        if (p.category === "accessories") {
          const vv = arr(p.accessory_variants);
          return vv.some(v => {
            const price = variantEffPrice(v);
            return price >= priceRange[0] && price <= priceRange[1];
          });
        }
        const ml = arr(p.milliliters);
        return ml.some(m => {
          const price = num(m.sale_price || m.price);
          return price >= priceRange[0] && price <= priceRange[1];
        });
      });
    }

    if (selectedCategories.length) f = f.filter(p => selectedCategories.includes(p.category));
    if (selectedGenders.length)    f = f.filter(p => selectedGenders.includes(p.sex));
    if (selectedAromas.length)     f = f.filter(p => selectedAromas.some(a => p.aroma_en && p.aroma_en.includes(a)));

    // collect min price per product for sorting
    f = f.map(p => {
      let prices = [];
      if (p.category === "accessories") {
        prices = arr(p.accessory_variants).map(v => variantEffPrice(v)).filter(v=>v>0);
      } else {
        prices = arr(p.milliliters).map(ml => num(ml.sale_price || ml.price)).filter(v=>v>0);
      }
      return {...p, _minPrice: prices.length ? Math.min(...prices) : 0};
    });
    if (sortOrder) {
      f = [...f].sort((a,b)=> sortOrder==="asc" ? (a._minPrice - b._minPrice) : (b._minPrice - a._minPrice));
    }
    return f;
  }, [perfumes, selectedCategories, selectedAromas, selectedGenders, isPriceActive, priceRange, sortOrder]);

  useEffect(()=>{ setFiltered(filteredMemo); }, [filteredMemo]);

  /* ---------- applied chips ---------- */
  useEffect(()=>{
    const chips = [
      ...selectedCategories.map(v => ({type:'category', value:v})),
      ...selectedAromas.map(v => ({type:'aroma', value:v})),
      sortOrder && {type:'sort', value: sortOrder === 'asc' ? 'Min to Max' : 'Max to Min'},
      isPriceActive && {type:'price', value: `${priceRange[0]} - ${priceRange[1]} ${t("newproducts.currency")}`},
      ...selectedGenders.map(v => ({type:'gender', value:v})),
    ].filter(Boolean);
    setApplied(chips);
  }, [selectedCategories, selectedAromas, selectedGenders, sortOrder, isPriceActive, priceRange, t]);

  const removeTag = (filter) => {
    if (filter.type==="aroma") setSelectedAromas(arr => arr.filter(a => a !== filter.value));
    if (filter.type==="price"){ setIsPriceActive(false); setPriceRange([minPrice, maxPrice]); }
    if (filter.type==="sort")  setSortOrder("");
    if (filter.type==="gender") setSelectedGenders(arr => arr.filter(g => g !== filter.value));
    if (filter.type==="category") setSelectedCategories(arr => arr.filter(c => c !== filter.value));
    setApplied(prev => prev.filter(f => !(f.type===filter.type && f.value===filter.value)));
  };
  const clearAll = () => {
    setSelectedAromas([]); setSortOrder(''); setApplied([]);
    setIsPriceActive(false); setPriceRange([minPrice, maxPrice]);
    setFiltered(perfumes); setSelectedGenders([]); setSelectedCategories([]);
  };

  const onPriceChange = (e) => {
    const value = Number(e.target.value);
    setIsPriceActive(true);
    if (e.target.name === "min") setPriceRange(([_, max]) => [value, max]);
    else setPriceRange(([min, _]) => [min, value]);
  };

  /* ---------- card helpers ---------- */
  const anySaleOnCard = (p) => {
    const mlSale = arr(p.milliliters).some(ml => Number(ml.sale_price) > 0 && Number(ml.sale_price) < Number(ml.price));
    const prodSale = Number(p.sale_p) > 0 && Number(p.sale_p) < Number(p.price_p);
    const accSale  = arr(p.accessory_variants).some(variantHasSale);
    return mlSale || prodSale || accSale;
  };

  if (loading) return <PageLoader><Spinner/></PageLoader>;

  const nameField = t("slider.name");
  const descField = language === "ka" ? "description_ka" : "description_en";
  const titleText =
    collection?.[nameField] ||
    collection?.name_ka ||
    collection?.name_en ||
    collection?.slug ||
    "";
  const descriptionText = collection?.[descField] || "";

  return (
    <>
      {/* HERO */}
      <div style={{width:"100%",display:"flex",justifyContent:"center",alignItems:"center",textAlign:"center",overflow:"hidden",maxHeight:"70vh",borderBottom:`1px solid ${COLORS.line}`,background:"#fff"}}>
        <img src={coverOf(collection||{})} alt="Collection cover" style={{width:"100%",height:"auto",display:"block"}}/>
      </div>

      {/* ABOUT */}
      <Container>
        <div style={{maxWidth:980, margin:"18px auto 28px", padding:"0 8px"}}>
          {titleText && <h2 style={{ margin: 0, marginBottom: 10, fontWeight: 900 }}>{titleText}</h2>}
          <p style={{ margin:0, color:COLORS.dim, lineHeight:1.6, whiteSpace:"pre-wrap" }}>
            {descriptionText || (language === "ka" ? "მონაცემები მალე დაემატება." : "Description coming soon.")}
          </p>
        </div>
      </Container>

      {/* STICKY CONTROლებ */}
      <TopBar>
        <TopBarInner>
          <LeftActions>
            <PillButton onClick={()=>setFiltersVisible(v=>!v)}><FaFilter/>{t("categorypage.filter")}</PillButton>
          </LeftActions>

          <RightActions ref={dropdownRef}>
            <SortDropdownWrap>
              <PillButton onClick={() => setSortDropdownVisible(p => !p)}><FaSort/>{t("categorypage.sort")}</PillButton>
              <DropdownCard $hidden={!sortDropdownVisible}>
                <DropdownItem onClick={() => { setSortOrder('asc'); setSortDropdownVisible(false); }}>
                  {t("categorypage.mintomax")}
                </DropdownItem>
                <DropdownItem onClick={() => { setSortOrder('desc'); setSortDropdownVisible(false); }}>
                  {t("categorypage.maxtomin")}
                </DropdownItem>
              </DropdownCard>
            </SortDropdownWrap>
          </RightActions>
        </TopBarInner>

        <AppliedFiltersBox>
          {applied.length ? (
            applied.map((filter, i) => (
              <FilterTag key={`${filter.type}-${filter.value}-${i}`}>
                {filter.type === "aroma" ? `${t("categorypage.aroma")}:`
                 : filter.type === "price" ? `${t("categorypage.price")}:`
                 : filter.type === "sort" ? `${t("categorypage.sort-filter")}:`
                 : filter.type === "gender" ? `${t("categorypage.gender")}:`
                 : filter.type === "category" ? `${t("categorypage.categories")}:`
                 : filter.type}
                &nbsp;{filter.type === "gender" ? t(`categorypage.${filter.value}`) : (filter.value)}
                <span className="close" onClick={() => removeTag(filter)}><FaTimes/></span>
              </FilterTag>
            ))
          ) : (
            <span style={{ color: COLORS.dim, fontSize: 13 }}>{t("categorypage.filteroff")}</span>
          )}
        </AppliedFiltersBox>
      </TopBar>

      {/* OVERLAY + DRAWER */}
      <Overlay $open={filtersVisible} onClick={() => setFiltersVisible(false)} />
      <FiltersMenu isVisible={filtersVisible} ref={menuRef}>
        <FilterTop>
          <h3>{t("categorypage.filter")}</h3>
          <CloseButton onClick={() => setFiltersVisible(false)} aria-label="Close"><FaTimes/></CloseButton>
        </FilterTop>

        <FilterBody>
          {/* Categories */}
          {!!categories.length && (
            <FilterSection>
              <FilterHeaderBtn onClick={()=>setCategoryExpanded(v=>!v)}>
                <span>{t("categorypage.categories")}</span>
                {categoryExpanded ? <FaChevronUp/> : <FaChevronDown/>}
              </FilterHeaderBtn>
              <FilterOptions isExpanded={categoryExpanded}>
                <div className="scroll">
                  {categories.map(cat=>(
                    <CheckboxItem key={cat}>
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat)}
                        onChange={(e)=> setSelectedCategories(prev => e.target.checked ? [...prev, cat] : prev.filter(c=>c!==cat))}
                      />
                      <span></span>{t(`categorypage.${cat}`)}
                    </CheckboxItem>
                  ))}
                </div>
              </FilterOptions>
            </FilterSection>
          )}

          {/* Gender */}
          <FilterSection>
            <FilterHeaderBtn onClick={()=>setGenderExpanded(v=>!v)}>
              <span>{t("categorypage.gender")}</span>
              {genderExpanded ? <FaChevronUp/> : <FaChevronDown/>}
            </FilterHeaderBtn>
            <FilterOptions isExpanded={genderExpanded}>
              <div className="scroll">
                {["man","woman","kids","unisex"].map(g=>(
                  <CheckboxItem key={g}>
                    <input
                      type="checkbox"
                      checked={selectedGenders.includes(g)}
                      onChange={(e)=> setSelectedGenders(prev => e.target.checked ? [...prev, g] : prev.filter(x=>x!==g))}
                    />
                    <span></span>{t(`categorypage.${g}`)}
                  </CheckboxItem>
                ))}
              </div>
            </FilterOptions>
          </FilterSection>

          {/* Aroma */}
          <FilterSection>
            <FilterHeaderBtn onClick={()=>setAromaExpanded(v=>!v)}>
              <span>{t("categorypage.aroma")}</span>
              {aromaExpanded ? <FaChevronUp/> : <FaChevronDown/>}
            </FilterHeaderBtn>
            <FilterOptions isExpanded={aromaExpanded} $tall>
              <div className="content">
                <SearchContainer>
                  <SearchInput
                    type="text"
                    placeholder={t("categorypage.searcharoma")}
                    onChange={(e) => setAromaSearch(e.target.value)}
                    value={aromaSearch}
                  />
                  <FaSearch/>
                </SearchContainer>
              </div>
              <div className="scroll">
                <CheckboxList>
                  {aromas
                    .filter((a) => a.toLowerCase().includes((aromaSearch||"").toLowerCase()))
                    .map((aroma) => (
                      <CheckboxItem key={aroma}>
                        <input
                          type="checkbox"
                          checked={selectedAromas.includes(aroma)}
                          onChange={(e)=>
                            setSelectedAromas(prev => e.target.checked ? [...prev, aroma] : prev.filter(x => x !== aroma))
                          }
                        />
                        <span></span>{aroma}
                      </CheckboxItem>
                    ))}
                </CheckboxList>
              </div>
            </FilterOptions>
          </FilterSection>

          {/* Price */}
          <FilterSection>
            <FilterHeaderBtn as="div"><span>{t("categorypage.price")}</span></FilterHeaderBtn>
            <div style={{padding:12}}>
              <PriceFilterContainer>
                <PriceRangeWrapper>
                  <PriceLabel>{t("categorypage.minprice")} <b>{priceRange[0]} {t("newproducts.currency")}</b></PriceLabel>
                  <PriceRangeInput type="range" name="min" min={minPrice} max={maxPrice || 0} value={priceRange[0]} onChange={onPriceChange}/>
                </PriceRangeWrapper>
                <PriceRangeWrapper>
                  <PriceLabel>{t("categorypage.maxprice")} <b>{priceRange[1]} {t("newproducts.currency")}</b></PriceLabel>
                  <PriceRangeInput type="range" name="max" min={minPrice} max={maxPrice || 0} value={priceRange[1]} onChange={onPriceChange}/>
                </PriceRangeWrapper>
              </PriceFilterContainer>
            </div>
          </FilterSection>
        </FilterBody>

        <DrawerFooter>
          <ClearFiltersButton onClick={clearAll}>{t("categorypage.clearfilters")}</ClearFiltersButton>
        </DrawerFooter>
      </FiltersMenu>

      {/* GRID */}
      <GridWrap>
        <PerfumeGrid>
          {filtered.slice(0, visible).map((p) => {
            const pid = p.product_id || p.id;

            const showAddToCart =
              p.category === "accessories"
                ? arr(p.accessory_variants).some(v => Number(v.is_active ?? 1) === 1 && Number(v.stock ?? 0) > 0)
                : canQuickAdd(p);

            return (
              <PerfumeCard
               key={pid}
               data-card="true"
               onClick={(e)=>{ e.stopPropagation(); setActiveCard(prev => (prev === pid ? null : pid)); }}
               $active={activeCard === pid}>
                {anySaleOnCard(p) && <SaleBadge>{t("newproducts.sale")}</SaleBadge>}

                <div className="hover-options" onClick={(e)=>{ e.stopPropagation(); setActiveCard(null); }}>
                  <CardLink
                    to={`/product/${p.product_id}`}
                    onMouseDown={(e)=>e.stopPropagation()}
                    onClick={(e)=>e.stopPropagation()}
                  >
                    <button className="view-btn">{t("categorypage.viewmore")}</button>
                  </CardLink>
                </div>

                <CardBody>
                  <ImgWrap>
                    <PerfumeImage src={cardImage(p)} alt={p.name}/>
                  </ImgWrap>

                  <PerfumeName title={p.name}>{p.name}</PerfumeName>
                  <PerfumeBrand title={p.brand}>{p.brand}</PerfumeBrand>

                  <PriceBlock>{priceRangeText(p, t)}</PriceBlock>
                </CardBody>

                <IconBar onClick={(e)=>e.stopPropagation()}>
                  {showAddToCart && (
                    <button
                      className="icon-btn"
                      onClick={(e)=>{
                        e.stopPropagation();

                        if (p.category === "accessories") {
                          const v = pickFirstAccessoryVariant(p);
                          if (!v) return;
                          addToCart({
                            ...p,
                            type: "accessory",
                            quantity: 1,
                            selectedVariant: { ...v, effective_price: variantEffPrice(v) },
                            accessory_variant_id: String(v.id),
                            full_stock: Number(v.stock || v.unit_stock || 0),
                            price_p: Number(v.price || 0),
                            sale_p: Number(v.sale_p || 0),
                            image: (Array.isArray(v.images) && v.images[0]) || (Array.isArray(p.imageUrls) && p.imageUrls[0]) || p.image || "",
                          });
                          return;
                        }

                        // ML-based → უსაფრთხო picker-ით, ფოლბექის გარეშე
// ML-based → FavoritesPage-ის მსგავსად (FULL-FIRST + quantities keyed by size)
// ML-based → FULL-FIRST არჩევანი (row-level stock-საც ამოწმებს)
// ... IconBar-ის addToCart onClick-ის perfume ბლოკში:
const pickedMl = getDefaultMlSelection(p);
if (!pickedMl) return;

const size = Number(pickedMl.size);

// სურათის არჩევა — ჯერ ML-ის სურათი, შემდეგ პროდუქტის
const imageFromMl =
  (Array.isArray(pickedMl.images) && pickedMl.images[0]) ||
  (Array.isArray(p.imageUrls) && p.imageUrls[0]) ||
  p.image ||
  "";

// კალათისთვის საჭირო სტრუქტურა (FavoritesPage-ის მსგავს სტილით)
const selectedMilliliters = [{
  size,
  price: Number(pickedMl.price || 0),
  sale_price: pickedMl.sale_price != null ? Number(pickedMl.sale_price) : null,
  images: Array.isArray(pickedMl.images) ? pickedMl.images : [],
  unit_stock: Number(pickedMl.unit_stock ?? 0),
  qty: 1,
}];

const quantities = { [String(size)]: 1 };

// 🔑 მნიშვნელოვანია: merge_key ერთნაირი უნდა იყოს, რომ რაოდენობა გაიზარდოს იმავე key-ზე
// 🔑 დავამატოთ რეჟიმი (unit|full), რომ არ აირიოს სხვადასხვა წყაროს დამატება
const mode = pickedMl._from === 'full' ? 'full' : 'unit';
const merge_key = `perfume:${p.product_id}:size:${size}:mode:${mode}`;

addToCart({
  ...p,
  type: "perfume",
  image: imageFromMl,                // ← გადააქვს სურათი ML-დან
  selectedMilliliters,
  quantities,
  merge_key,                         // ← გადააქვს merge key (რაოდენობის გასაზრდელად)
  use_full_stock: pickedMl._from === 'full',  // სურვილისამებრ: შეგიძლია გამოიყენო UI/ლოჯიკში
  picked_from: pickedMl._from,  
  stock_strategy: pickedMl.stock_strategy ?? pickedMl._strat ?? null,              // 'full' | 'unit' დიბაგისთვის
});
                      }}
                      aria-label={t("categorypage.addtocart") || "Add to cart"}
                    >
                      <ShoppingCart size={18}/>
                    </button>
                  )}
                  <div className="icon-btn">
                    <FavoriteButton productId={p.product_id} initialIsFavorite={p.is_favorite}/>
                  </div>
                </IconBar>
              </PerfumeCard>
            );
          })}
        </PerfumeGrid>

        {visible < filtered.length && (
          <LoadMoreButton onClick={()=>setVisible(v => v + 8)}>
            {t("purchases.loadmore")}
          </LoadMoreButton>
        )}
      </GridWrap>
    </>
  );
};

export default CollectionPage;
